import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, startLogin } from "@/lib/auth";
import { resolveImageUrl } from "@/lib/api";
import { LogIn, LogOut, Shield, Mail, Menu, X, Globe2, Search } from "lucide-react";
import SearchModal from "@/components/SearchModal";
import LanguageSelector from "@/components/LanguageSelector";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_territoria-wiki/artifacts/daxzxmyo_territoria%20logo.png";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);
  const openSearch = () => setSearchOpen(true);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Cmd/Ctrl+K shortcut to open search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="relative z-30 border-b border-[var(--t-border)]" data-testid="site-header">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1428]/95 to-[#050912]/80 backdrop-blur" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4 md:gap-6 flex-nowrap">
          {/* ─── Logo / branding container ────────────────────── */}
          <Link to="/" className="flex items-center gap-2.5 md:gap-3 group flex-shrink-0 min-w-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Territoria" className="h-10 w-10 md:h-12 md:w-12 object-contain drop-shadow-[0_0_12px_rgba(58,160,255,0.5)] flex-shrink-0" />
            <div className="flex flex-col leading-none min-w-0">
              <span className="font-display text-base md:text-xl tracking-[0.14em] md:tracking-[0.18em] text-[var(--t-cyan)] whitespace-nowrap">TERRITORIA</span>
              <span className="hidden xl:block font-mono text-[10px] tracking-[0.3em] text-[var(--t-text-muted)] uppercase whitespace-nowrap">// geopolitical mmo</span>
            </div>
          </Link>

          {/* ─── Nav links container — hidden below xl (hamburger takes over) ─── */}
          <nav className="hidden xl:flex items-center gap-1 xl:gap-1.5 flex-shrink-0 mx-auto" data-testid="main-nav">
            <NavItem to="/" label={t("nav.home")} end testid="nav-home" />
            <NavItem to="/about" label={t("nav.about")} testid="nav-about" />
            <NavItem to="/wiki" label={t("nav.wiki")} testid="nav-wiki" />
            <NavItem to="/forum" label={t("nav.forum")} testid="nav-forum" />
            <NavItem to="/rankings" label={t("nav.rankings")} testid="nav-rankings" />
          </nav>

          {/* ─── Right cluster (search / play / lang / auth / menu) ─── */}
          <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
            {/* Desktop search pill — only at 2xl+ to keep room for nav + admin cluster */}
            <button
              onClick={openSearch}
              className="hidden 2xl:inline-flex items-center gap-2 px-3 py-2 bg-[rgba(5,12,28,0.6)] border border-[var(--t-border)] hover:border-[var(--t-blue)] transition group min-w-[160px]"
              data-testid="search-trigger-desktop"
            >
              <Search className="w-3.5 h-3.5 text-[var(--t-blue-glow)]" />
              <span className="text-xs text-[var(--t-text-muted)] font-mono tracking-[0.1em] flex-1 text-left truncate">{t("nav.search")}</span>
              <kbd className="font-mono text-[10px] px-1.5 py-0.5 border border-[var(--t-border)] text-[var(--t-text-muted)] flex-shrink-0">⌘K</kbd>
            </button>
            {/* Compact search icon — shown below 2xl */}
            <button
              onClick={openSearch}
              className="2xl:hidden inline-flex items-center justify-center w-10 h-10 border border-[var(--t-border-glow)] text-[var(--t-blue-glow)] hover:text-[var(--t-cyan)] hover:border-[var(--t-blue)] transition flex-shrink-0"
              data-testid="search-trigger-mobile"
              aria-label="Open search"
            >
              <Search className="w-4 h-4" />
            </button>

            <a href="https://territoria.world" target="_blank" rel="noreferrer" className="hidden sm:inline-flex btn-primary !py-2 !px-3 md:!px-4 !text-xs flex-shrink-0" data-testid="play-now-btn">
              {t("nav.playNow")}
            </a>
            <LanguageSelector />
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                {user.is_admin && (
                  <button onClick={() => navigate("/admin")} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="admin-btn">
                    <Shield className="w-3.5 h-3.5" /> {t("nav.admin")}
                  </button>
                )}
                <button
                  onClick={() => navigate("/profile")}
                  className="inline-flex items-center gap-2 px-2.5 py-1.5 border border-[var(--t-border)] hover:border-[var(--t-blue)] transition group"
                  data-testid="profile-btn"
                  title="Profile"
                >
                  {(user.profile_picture || user.picture) ? (
                    <img src={resolveImageUrl(user.profile_picture || user.picture)} alt="" className="w-6 h-6 rounded-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-[rgba(58,160,255,0.1)] border border-[var(--t-border-glow)] flex items-center justify-center text-[var(--t-blue-glow)] font-mono text-[10px]">
                      {(user.name || user.username || "C").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <span className="font-display text-[10px] tracking-[0.14em] uppercase text-[var(--t-cyan)] group-hover:text-[var(--t-blue-glow)] max-w-[60px] lg:max-w-[100px] truncate">{user.name || user.username || "Profile"}</span>
                </button>
                <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="logout-btn">
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button onClick={startLogin} className="hidden md:inline-flex btn-ghost !py-2 !px-3 !text-xs" data-testid="login-btn">
                <LogIn className="w-3.5 h-3.5" /> {t("nav.signIn")}
              </button>
            )}

            {/* Mobile / tablet menu trigger — matches nav breakpoint */}
            <button
              onClick={() => setMenuOpen(true)}
              className="xl:hidden inline-flex items-center justify-center w-10 h-10 border border-[var(--t-border-glow)] text-[var(--t-blue-glow)] hover:text-[var(--t-cyan)] hover:border-[var(--t-blue)] transition"
              data-testid="menu-open-btn"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 xl:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#050912]/85 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <aside className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-gradient-to-b from-[#0a1428] to-[#050912] border-l border-[var(--t-border)] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[var(--t-border)]">
              <span className="font-mono text-[11px] tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">// menu</span>
              <button onClick={() => setMenuOpen(false)} className="w-9 h-9 inline-flex items-center justify-center text-[var(--t-text-dim)] hover:text-[var(--t-cyan)]" data-testid="menu-close-btn" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              <MobileLink to="/" label={t("nav.home")} end onNavigate={closeMenu} testid="m-nav-home" />
              <MobileLink to="/about" label={t("nav.about")} onNavigate={closeMenu} testid="m-nav-about" />
              <MobileLink to="/wiki" label={t("nav.wiki")} onNavigate={closeMenu} testid="m-nav-wiki" />
              <MobileLink to="/forum" label={t("nav.forum")} onNavigate={closeMenu} testid="m-nav-forum" />
              <MobileLink to="/rankings" label={t("nav.rankings")} onNavigate={closeMenu} testid="m-nav-rankings" />

              <div className="pt-4 mt-4 border-t border-[var(--t-border)] space-y-2">
                <div className="px-1"><LanguageSelector /></div>
                <button onClick={() => { closeMenu(); openSearch(); }} className="btn-ghost w-full !justify-center" data-testid="m-search-btn">
                  <Search className="w-4 h-4" /> {t("nav.search")}
                </button>
                <a href="https://territoria.world" target="_blank" rel="noreferrer" className="btn-primary w-full !justify-center" data-testid="m-play-now-btn">
                  <Globe2 className="w-4 h-4" /> {t("nav.playNow")}
                </a>
                {user ? (
                  <>
                    {user.is_admin && (
                      <button onClick={() => { setMenuOpen(false); navigate("/admin"); }} className="btn-ghost w-full !justify-center" data-testid="m-admin-btn">
                        <Shield className="w-4 h-4" /> {t("nav.admin")}
                      </button>
                    )}
                    <button onClick={async () => { setMenuOpen(false); await logout(); navigate("/"); }} className="btn-ghost w-full !justify-center" data-testid="m-logout-btn">
                      <LogOut className="w-4 h-4" /> {t("nav.signOut")}
                    </button>
                    <div className="pt-3 mt-3 border-t border-[var(--t-border)] text-center">
                      <div className="text-xs font-mono text-[var(--t-text-muted)] tracking-[0.18em] uppercase">{t("auth.loggedInAs")}</div>
                      <div className="text-sm text-[var(--t-cyan)] font-mono mt-1 break-all">{user.email}</div>
                    </div>
                  </>
                ) : (
                  <button onClick={startLogin} className="btn-ghost w-full !justify-center" data-testid="m-login-btn">
                    <LogIn className="w-4 h-4" /> {t("auth.signInWith")}
                  </button>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-[var(--t-border)] text-center">
              <span className="font-mono text-[10px] tracking-[0.3em] text-[var(--t-text-muted)] uppercase">territoria.wiki</span>
            </div>
          </aside>
        </div>
      )}

      <main className="relative flex-1 z-10">{children}</main>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />

      <footer className="relative z-10 mt-16 md:mt-24 border-t border-[var(--t-border)] bg-[#050912]/90" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src={LOGO_URL} alt="" className="h-10 w-10 opacity-90" />
              <span className="font-display text-lg tracking-[0.18em] text-[var(--t-cyan)]">TERRITORIA</span>
            </div>
            <p className="text-sm text-[var(--t-text-dim)] leading-relaxed">
              Forge your nation. Command armies. Reshape the world. The official community wiki for the geopolitical MMO.
            </p>
          </div>
          <div>
            <h4 className="font-display text-sm tracking-[0.18em] text-[var(--t-blue-glow)] uppercase mb-3">Navigate</h4>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/wiki" className="text-[var(--t-steel)] hover:text-[var(--t-cyan)] transition">Wiki</Link></li>
              <li><Link to="/contribute" className="text-[var(--t-steel)] hover:text-[var(--t-cyan)] transition">Contribute a Tip</Link></li>
              <li><a href="https://territoria.world" target="_blank" rel="noreferrer" className="text-[var(--t-steel)] hover:text-[var(--t-cyan)] transition">Play the Game</a></li>
              <li><a href="https://discord.gg/territoria" target="_blank" rel="noreferrer" className="text-[var(--t-steel)] hover:text-[var(--t-cyan)] transition">Discord Community</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm tracking-[0.18em] text-[var(--t-blue-glow)] uppercase mb-3">Stay in the Field</h4>
            <p className="text-xs text-[var(--t-text-dim)] mb-3">Get patch notes, lore drops, and meta updates.</p>
            <NewsletterMini />
          </div>
        </div>
        <div className="border-t border-[var(--t-border)] py-4 text-center text-[10px] sm:text-xs font-mono text-[var(--t-text-muted)] tracking-[0.2em] uppercase px-4">
          © Territoria · territoria.wiki · Built by the Citizens
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, label, end, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `relative px-4 py-2 text-sm font-display tracking-[0.14em] uppercase transition ${
          isActive ? "text-[var(--t-cyan)]" : "text-[var(--t-steel)] hover:text-[var(--t-blue-glow)]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {label}
          {isActive && <span className="absolute -bottom-px left-3 right-3 h-px bg-[var(--t-blue)] shadow-[0_0_8px_var(--t-blue)]" />}
        </>
      )}
    </NavLink>
  );
}

function MobileLink({ to, label, end, testid, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={testid}
      className={({ isActive }) =>
        `block px-4 py-3 font-display text-base tracking-[0.16em] uppercase border-l-2 transition ${
          isActive
            ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
            : "border-transparent text-[var(--t-steel)] hover:bg-[rgba(58,160,255,0.06)] hover:text-[var(--t-blue-glow)]"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function NewsletterMini() {
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const email = fd.get("email");
        if (!email) return;
        try {
          const { default: client } = await import("@/lib/api");
          await client.post("/newsletter", { email });
          e.currentTarget.reset();
          alert("Subscribed. Welcome, Citizen.");
        } catch { alert("Could not subscribe."); }
      }}
      className="flex gap-2"
      data-testid="newsletter-form"
    >
      <input name="email" type="email" required placeholder="commander@territoria.wiki" className="field !py-2 !text-sm min-w-0 flex-1" data-testid="newsletter-email" />
      <button type="submit" className="btn-primary !py-2 !px-3 flex-shrink-0" data-testid="newsletter-submit"><Mail className="w-4 h-4" /></button>
    </form>
  );
}
